import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import authService from "../services/authService";

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const labelClassName =
  "mb-2 flex items-center gap-1 text-sm font-semibold text-slate-200";

const RequiredMark = () => (
  <span className="text-sm font-semibold text-rose-400" aria-hidden="true">
    *
  </span>
);

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    password_confirm: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    organization: "",
    job_title: "",
  });
  const [countryCode, setCountryCode] = useState("+91");
  const [captcha, setCaptcha] = useState(() =>
    Math.floor(1000 + Math.random() * 9000)
  );
  const [captchaInput, setCaptchaInput] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    type: "success",
    message: "",
  });

  const refreshCaptcha = () => {
    setCaptcha(Math.floor(1000 + Math.random() * 9000));
    setCaptchaInput("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        name === "phone_number" ? value.replace(/\D/g, "").slice(0, 15) : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      password_confirm: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      organization: "",
      job_title: "",
    });
    setCaptchaInput("");
    setAcceptTerms(false);
    refreshCaptcha();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setModal({ isOpen: false, type: "success", message: "" });

    if (captchaInput.trim() !== captcha.toString()) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Invalid captcha. Please try again.",
      });
      refreshCaptcha();
      return;
    }

    if (formData.password !== formData.password_confirm) {
      setModal({
        isOpen: true,
        type: "error",
        message: "Passwords do not match.",
      });
      return;
    }

    setLoading(true);

    try {
      const signupData = {
        email: formData.email.trim(),
        password: formData.password,
        password_confirm: formData.password_confirm,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone_number: `${countryCode}${formData.phone_number}`,
        organization: formData.organization.trim(),
        job_title: formData.job_title.trim(),
      };

      const response = await authService.signup(signupData);

      if (response.status === "success") {
        setModal({
          isOpen: true,
          type: "success",
          message:
            response.message ||
            "Registration successful! Please check your email to verify your account. Redirecting to login...",
        });

        resetForm();

        setTimeout(() => {
          navigate("/login");
        }, 3000);
        return;
      }

      let errorMessage =
        response.message || "Registration failed. Please try again.";

      if (response.errors) {
        const errorDetails = Object.entries(response.errors)
          .map(
            ([field, messages]) =>
              `${field}: ${
                Array.isArray(messages) ? messages.join(", ") : messages
              }`
          )
          .join("\n");
        errorMessage = `${errorMessage}\n${errorDetails}`;
      }

      setModal({ isOpen: true, type: "error", message: errorMessage });
    } catch (error) {
      console.error("Registration error:", error);
      setModal({
        isOpen: true,
        type: "error",
        message:
          error.message ||
          "An error occurred during registration. Please try again.",
      });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const modalClassName =
    modal.type === "error"
      ? "border-rose-400/35 bg-rose-500/10 text-rose-100"
      : "border-emerald-400/35 bg-emerald-500/10 text-emerald-100";

  return (
    <>
    <main className="min-h-screen bg-[#111318] text-slate-200">
      <Header />

      <section className="flex justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <div className="w-full max-w-4xl">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 gap-4 rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-5 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8 md:grid-cols-2 md:gap-x-5 md:gap-y-5"
          >
            <div className="mb-1 text-center md:col-span-2">
              <h2 className="text-2xl font-bold tracking-[-0.02em] text-slate-50 sm:text-[30px]">
                Create your account
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Join Limbus Intelligence and start exploring research data.
              </p>
            </div>

            {modal.isOpen && (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm leading-6 whitespace-pre-line ${modalClassName} md:col-span-2`}
                role="alert"
              >
                {modal.message}
              </div>
            )}

            <div>
              <label htmlFor="first_name" className={labelClassName}>
                First Name <RequiredMark />
              </label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                placeholder="First Name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className={inputClassName}
              />
            </div>

            <div>
              <label htmlFor="last_name" className={labelClassName}>
                Last Name
              </label>
              <input
                id="last_name"
                type="text"
                name="last_name"
                placeholder="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div>
              <label htmlFor="email" className={labelClassName}>
                Email <RequiredMark />
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClassName}
              />
            </div>

            <div>
              <label htmlFor="phone_number" className={labelClassName}>
                Phone Number <RequiredMark />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  aria-label="Country code"
                  value={countryCode}
                  onChange={(event) => setCountryCode(event.target.value)}
                  className="w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20 sm:w-[104px] sm:shrink-0"
                >
                  <option value="+91">+91</option>
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                </select>

                <input
                  id="phone_number"
                  type="tel"
                  name="phone_number"
                  placeholder="Phone"
                  value={formData.phone_number}
                  onChange={handleChange}
                  required
                  className={inputClassName}
                />
              </div>
            </div>

            <div>
              <label htmlFor="organization" className={labelClassName}>
                Organization
              </label>
              <input
                id="organization"
                type="text"
                name="organization"
                placeholder="Organization"
                value={formData.organization}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div>
              <label htmlFor="job_title" className={labelClassName}>
                Job Title
              </label>
              <input
                id="job_title"
                type="text"
                name="job_title"
                placeholder="Job Title"
                value={formData.job_title}
                onChange={handleChange}
                className={inputClassName}
              />
            </div>

            <div>
              <label htmlFor="password" className={labelClassName}>
                Password <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`${inputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="password_confirm" className={labelClassName}>
                Confirm Password <RequiredMark />
              </label>
              <div className="relative">
                <input
                  id="password_confirm"
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirm"
                  placeholder="Confirm Password"
                  value={formData.password_confirm}
                  onChange={handleChange}
                  required
                  className={`${inputClassName} pr-12`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword((current) => !current)
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 transition hover:text-white"
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="captcha" className={labelClassName}>
                Captcha <RequiredMark />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="captcha"
                  type="text"
                  placeholder="Enter captcha"
                  value={captchaInput}
                  onChange={(event) => setCaptchaInput(event.target.value)}
                  required
                  className={inputClassName}
                />

                <div className="flex min-w-[148px] items-center justify-between rounded-xl bg-blue-600 px-4 py-3 font-semibold tracking-[0.22em] text-white sm:justify-center sm:gap-3">
                  <span className="text-sm">{captcha}</span>
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    title="Refresh Captcha"
                    className="rounded-full bg-white/10 px-2 py-1 text-sm tracking-normal transition hover:bg-white/20"
                  >
                    ↻
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-300 md:col-span-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptTerms}
                onChange={(event) => setAcceptTerms(event.target.checked)}
                required
                className="mt-1 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500"
              />
              <label htmlFor="terms" className="leading-6">
                I agree to the{" "}
                <Link
                  to="/legal-docs"
                  className="font-semibold text-blue-400 transition hover:text-blue-300"
                >
                  Terms & Conditions
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={!acceptTerms || loading}
              className="w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600 md:col-span-2"
            >
              {loading ? "Creating Account..." : "Sign Up"}
            </button>

            <div className="text-center text-sm text-slate-400 md:col-span-2">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-semibold text-blue-400 transition hover:text-blue-300"
              >
                Login
              </Link>
            </div>
          </form>
        </div>
      </section>
    </main>
  <Footer />
  </>
  );
};

export default Signup;
